import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { callApi } from '../../utils/apiCall';
import { updateError } from './user';

const initialState = {
    isLoading: false,
    isSyncing: false,
    accounts: [],
    availableProperties: [],
    properties: [],
    summary: null,
    dailyMetrics: [],
    dailyMetricsLoading: false,
    events: [],
    eventsLoading: false,
    sources: [],
    sourcesLoading: false,
    comparison: null,
    comparisonLoading: false,
    crossPractice: null,
    crossPracticeLoading: false,
};

const slice = createSlice({
    name: 'googleAnalytics',
    initialState,
    reducers: {
        startLoading(state) {
            state.isLoading = true;
        },
        hasError(state) {
            state.isLoading = false;
        },
        setAccounts(state, action) {
            state.isLoading = false;
            state.accounts = action.payload;
        },
        setAvailableProperties(state, action) {
            state.availableProperties = action.payload;
        },
        setProperties(state, action) {
            state.properties = action.payload;
        },
        setSummary(state, action) {
            state.summary = action.payload;
        },
        setIsSyncing(state, action) {
            state.isSyncing = action.payload;
        },
        startDailyMetricsLoading(state) {
            state.dailyMetricsLoading = true;
        },
        setDailyMetrics(state, action) {
            state.dailyMetricsLoading = false;
            state.dailyMetrics = action.payload;
        },
        failDailyMetrics(state) {
            state.dailyMetricsLoading = false;
        },
        startEventsLoading(state) {
            state.eventsLoading = true;
        },
        setEvents(state, action) {
            state.eventsLoading = false;
            state.events = action.payload;
        },
        failEvents(state) {
            state.eventsLoading = false;
        },
        startSourcesLoading(state) {
            state.sourcesLoading = true;
        },
        setSources(state, action) {
            state.sourcesLoading = false;
            state.sources = action.payload;
        },
        failSources(state) {
            state.sourcesLoading = false;
        },
        startComparisonLoading(state) {
            state.comparisonLoading = true;
        },
        setComparison(state, action) {
            state.comparisonLoading = false;
            state.comparison = action.payload;
        },
        failComparison(state) {
            state.comparisonLoading = false;
        },
        startCrossPracticeLoading(state) {
            state.crossPracticeLoading = true;
        },
        setCrossPractice(state, action) {
            state.crossPracticeLoading = false;
            state.crossPractice = action.payload;
        },
        failCrossPractice(state) {
            state.crossPracticeLoading = false;
        },
        resetState() {
            return initialState;
        },
    },
});

export const {
    startLoading,
    hasError,
    setAccounts,
    setAvailableProperties,
    setProperties,
    setSummary,
    setIsSyncing,
    startDailyMetricsLoading,
    setDailyMetrics,
    failDailyMetrics,
    startEventsLoading,
    setEvents,
    failEvents,
    startSourcesLoading,
    setSources,
    failSources,
    startComparisonLoading,
    setComparison,
    failComparison,
    startCrossPracticeLoading,
    setCrossPractice,
    failCrossPractice,
    resetState,
} = slice.actions;

export default slice.reducer;

// --------------- Thunks ---------------

/**
 * Fetch all connected Google Analytics accounts for this practice
 */
export function fetchGoogleAnalyticsAccounts(practiceId) {
    return async () => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-analytics-accounts/`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setAccounts(response.data?.results || response.data || []));
            } else {
                dispatch(slice.actions.hasError());
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to fetch Google Analytics accounts' }));
            dispatch(slice.actions.hasError());
        }
    };
}

/**
 * Get the OAuth URL and redirect the user to Google's consent screen
 */
export function getAuthUrl(practiceId) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-analytics-accounts/auth_url/`
            );
            if (response.status === 200 && response.data?.auth_url) {
                window.location.href = response.data.auth_url;
            } else {
                dispatch(updateError({ success: false, message: 'Failed to get Google Analytics authorization URL' }));
                dispatch(slice.actions.hasError());
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to start Google Analytics connection' }));
            dispatch(slice.actions.hasError());
        }
    };
}

/**
 * Exchange the OAuth authorization code for tokens (callback handler)
 */
export function exchangeAuthCode(practiceId, code, onSuccess, onError) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-analytics-accounts/callback/`,
                'post',
                { code }
            );
            if (response.status === 200 || response.status === 201) {
                dispatch(updateError({ success: true, message: 'Google Analytics account connected successfully' }));
                if (typeof onSuccess === 'function') onSuccess(response.data);
            } else {
                dispatch(updateError({ success: false, message: 'Failed to connect Google Analytics account' }));
                if (typeof onError === 'function') {
                    onError(response.data?.error || 'Failed to connect Google Analytics account');
                } else {
                    dispatch(slice.actions.hasError());
                }
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Google Analytics authorization failed' }));
            if (typeof onError === 'function') {
                onError(error?.response?.data?.error || error?.message || 'Unknown error');
            } else {
                dispatch(slice.actions.hasError());
            }
        }
    };
}

/**
 * Fetch available GA4 properties for a connected account
 */
export function fetchAvailableProperties(practiceId, accountId) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-analytics-accounts/${accountId}/available_properties/`
            );
            if (response.status === 200) {
                // Backend returns { properties: [...] }. Fall back to results / bare
                // array for safety. Must end up an array — PropertySelector requires
                // Array.isArray(availableProperties) or it shows "No properties found".
                dispatch(slice.actions.setAvailableProperties(
                    response.data?.properties || response.data?.results || response.data || []
                ));
            } else {
                dispatch(slice.actions.hasError());
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to fetch available properties' }));
            dispatch(slice.actions.hasError());
        }
    };
}

/**
 * Connect selected GA4 properties to start syncing data
 */
export function connectProperties(practiceId, accountId, propertyIds, onSuccess) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-analytics-accounts/${accountId}/connect_properties/`,
                'post',
                { property_ids: propertyIds }
            );
            if (response.status === 200 || response.status === 201) {
                dispatch(updateError({ success: true, message: 'Google Analytics properties connected successfully' }));
                if (typeof onSuccess === 'function') onSuccess(response.data);
            } else {
                dispatch(updateError({ success: false, message: 'Failed to connect properties' }));
                dispatch(slice.actions.hasError());
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to connect properties' }));
            dispatch(slice.actions.hasError());
        }
    };
}

/**
 * Disconnect a Google Analytics account and reset slice state
 */
export function disconnectAccount(practiceId, accountId, onSuccess) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-analytics-accounts/${accountId}/disconnect/`,
                'post'
            );
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Google Analytics account disconnected' }));
                dispatch(slice.actions.resetState());
                if (typeof onSuccess === 'function') onSuccess();
            } else {
                dispatch(updateError({ success: false, message: 'Failed to disconnect account' }));
                dispatch(slice.actions.hasError());
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to disconnect account' }));
            dispatch(slice.actions.hasError());
        }
    };
}

/**
 * Fetch connected GA4 properties for an account
 */
export function fetchProperties(practiceId, accountId) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-analytics-accounts/${accountId}/properties/`
            );
            if (response.status === 200) {
                const results = Array.isArray(response.data)
                    ? response.data
                    : (response.data?.results || []);
                dispatch(slice.actions.setProperties(results));
            } else {
                dispatch(slice.actions.hasError());
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to fetch properties' }));
            dispatch(slice.actions.hasError());
        }
    };
}

/**
 * Trigger a manual sync for a GA4 property (Celery task — wait 3s before clearing syncing flag)
 */
export function syncProperty(practiceId, accountId, propertyPk) {
    return async () => {
        dispatch(slice.actions.setIsSyncing(true));
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-analytics-accounts/${accountId}/properties/${propertyPk}/sync/`,
                'post'
            );
            if (response.status === 200 || response.status === 202) {
                dispatch(updateError({ success: true, message: 'Sync started. Data will refresh shortly.' }));
            } else {
                dispatch(updateError({ success: false, message: 'Failed to trigger property sync' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to sync property' }));
        } finally {
            setTimeout(() => {
                dispatch(slice.actions.setIsSyncing(false));
            }, 3000);
        }
    };
}

/**
 * Fetch aggregated summary metrics for a GA4 property
 */
export function fetchPropertySummary(practiceId, accountId, propertyPk, params = {}) {
    return async () => {
        try {
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    queryParams.append(key, value);
                }
            });
            const queryString = queryParams.toString();
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-analytics-accounts/${accountId}/properties/${propertyPk}/summary/${queryString ? `?${queryString}` : ''}`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setSummary(response.data));
            } else {
                dispatch(updateError({ success: false, message: 'Failed to fetch property summary' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to fetch property summary' }));
        }
    };
}

/**
 * Fetch daily metrics for a GA4 property
 */
export function fetchDailyMetrics(practiceId, accountId, propertyPk, params = {}) {
    return async () => {
        dispatch(slice.actions.startDailyMetricsLoading());
        try {
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    queryParams.append(key, value);
                }
            });
            const queryString = queryParams.toString();
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-analytics-accounts/${accountId}/properties/${propertyPk}/daily-metrics/${queryString ? `?${queryString}` : ''}`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setDailyMetrics(response.data?.results || []));
            } else {
                dispatch(slice.actions.failDailyMetrics());
                dispatch(updateError({ success: false, message: 'Failed to fetch daily metrics' }));
            }
        } catch (error) {
            dispatch(slice.actions.failDailyMetrics());
            dispatch(updateError({ success: false, message: error.message || 'Failed to fetch daily metrics' }));
        }
    };
}

/**
 * Fetch period-over-period comparison metrics for a GA4 property
 */
export function fetchComparison(practiceId, accountId, propertyPk, params = {}) {
    return async () => {
        dispatch(slice.actions.startComparisonLoading());
        try {
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    queryParams.append(key, value);
                }
            });
            const queryString = queryParams.toString();
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-analytics-accounts/${accountId}/properties/${propertyPk}/daily-metrics/compare/${queryString ? `?${queryString}` : ''}`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setComparison(response.data));
            } else {
                dispatch(slice.actions.failComparison());
                dispatch(updateError({ success: false, message: 'Failed to fetch comparison data' }));
            }
        } catch (error) {
            dispatch(slice.actions.failComparison());
            dispatch(updateError({ success: false, message: error.message || 'Failed to fetch comparison data' }));
        }
    };
}

/**
 * Fetch GA4 events for a property
 */
export function fetchEvents(practiceId, accountId, propertyPk, params = {}) {
    return async () => {
        dispatch(slice.actions.startEventsLoading());
        try {
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    queryParams.append(key, value);
                }
            });
            const queryString = queryParams.toString();
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-analytics-accounts/${accountId}/properties/${propertyPk}/events/${queryString ? `?${queryString}` : ''}`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setEvents(response.data?.results || []));
            } else {
                dispatch(slice.actions.failEvents());
                dispatch(updateError({ success: false, message: 'Failed to fetch events' }));
            }
        } catch (error) {
            dispatch(slice.actions.failEvents());
            dispatch(updateError({ success: false, message: error.message || 'Failed to fetch events' }));
        }
    };
}

/**
 * Fetch traffic sources for a GA4 property
 */
export function fetchSources(practiceId, accountId, propertyPk, params = {}) {
    return async () => {
        dispatch(slice.actions.startSourcesLoading());
        try {
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    queryParams.append(key, value);
                }
            });
            const queryString = queryParams.toString();
            const response = await callApi(
                `/api/v1/practices/${practiceId}/google-analytics-accounts/${accountId}/properties/${propertyPk}/sources/${queryString ? `?${queryString}` : ''}`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setSources(response.data?.results || []));
            } else {
                dispatch(slice.actions.failSources());
                dispatch(updateError({ success: false, message: 'Failed to fetch sources' }));
            }
        } catch (error) {
            dispatch(slice.actions.failSources());
            dispatch(updateError({ success: false, message: error.message || 'Failed to fetch sources' }));
        }
    };
}

/**
 * Fetch cross-practice Google Analytics comparison (global endpoint, no practiceId)
 */
export function fetchCrossPracticeComparison(params = {}) {
    return async () => {
        dispatch(slice.actions.startCrossPracticeLoading());
        try {
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    queryParams.append(key, value);
                }
            });
            const queryString = queryParams.toString();
            const response = await callApi(
                `/api/v1/google-analytics/cross-practice-compare/compare/${queryString ? `?${queryString}` : ''}`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setCrossPractice(response.data));
            } else {
                dispatch(slice.actions.failCrossPractice());
                dispatch(updateError({ success: false, message: 'Failed to fetch cross-practice comparison' }));
            }
        } catch (error) {
            dispatch(slice.actions.failCrossPractice());
            dispatch(updateError({ success: false, message: error.message || 'Failed to fetch cross-practice comparison' }));
        }
    };
}
